<template>
  <div class="diy-page">

    <!-- 授权遮罩：未授权时盖住整页 -->
    <div v-if="authChecked && !diyAuthorized" class="unauth-mask">
      <div class="unauth-dialog">
        <div class="unauth-title">本插件未授权</div>
        <div class="unauth-desc">无法使用，请联系您的上级授权使用！</div>
      </div>
    </div>


    <div class="topbar">
      <div class="left">
        <el-tag type="info">商户ID: {{ storeId }}</el-tag>

        <!-- 选择小程序 -->
        <el-select v-model="miniId" placeholder="选择小程序" style="width:260px;margin-left:8px" @change="onMiniChange">
          <el-option v-for="m in minis" :key="m.mini_id" :label="miniLabel(m)" :value="m.mini_id" />
        </el-select>

        <el-select v-model="routeKey" placeholder="路由" style="width:160px;margin-left:8px" @change="loadPages">
          <el-option label="首页(home)" value="home" />
          <el-option label="落地页1(landing_1)" value="landing_1" />
          <el-option label="落地页2(landing_2)" value="landing_2" />
        </el-select>

        <el-input v-model="pageName" style="width:220px;margin-left:8px" placeholder="页面名称">
          <template #prefix><el-icon>
              <Document />
            </el-icon></template>
        </el-input>

        <el-button style="margin-left:8px" @click="createNew">新建</el-button>
        <el-button @click="loadPages" :disabled="!miniId">我的页面</el-button>
        <el-button @click="openTemplateLib">模板库</el-button>
      </div>
      <div class="right">
        <el-button @click="saveDraft" :loading="saving" :disabled="!miniId">保存</el-button>
        <el-button type="success" @click="publish" :loading="publishing" :disabled="!miniId">发布</el-button>
      </div>
    </div>

    <div class="workspace">
      <!-- 组件库 -->
      <div class="panel palette">
        <div class="panel-title">组件库</div>
        <div class="palette-list">
          <div class="palette-item" v-for="c in catalog" :key="c.type" @click="addBlock(c)">
            <el-icon class="icon">
              <component :is="c.icon" />
            </el-icon>
            <div class="name">{{ c.name }}</div>
          </div>
        </div>
      </div>

      <!-- 画布 -->
      <div class="panel canvas">
        <div class="phone">
          <div class="safe-area">
            <draggable v-model="blocks" item-key="id" :animation="200" handle=".drag-handle">
              <template #item="{ element, index }">
                <div :class="['block', { active: activeId === element.id }]" @click="activeId = element.id">
                  <div class="toolbar">
                    <span class="drag-handle">⋮⋮</span>
                    <el-button size="small" text @click.stop="copyBlock(index)">复制</el-button>
                    <el-button size="small" text type="danger" @click.stop="removeBlock(index)">删除</el-button>
                    <el-button size="small" text @click.stop="moveUp(index)" :disabled="index === 0">上移</el-button>
                    <el-button size="small" text @click.stop="moveDown(index)"
                      :disabled="index === blocks.length - 1">下移</el-button>
                  </div>

                  <!-- 与小程序一致：容器是否白底/内边距由组件类型决定 -->
                  <div class="block-body" :style="blockStyle(element)">
                    <!-- H1 -->
                    <template v-if="element.type === 'h1'">
                      <div
                        :style="{ textAlign: element.props.align, fontSize: element.props.fontSize + 'px', color: element.props.color, fontWeight: '700' }">
                        {{ element.props.text }}
                      </div>
                    </template>

                    <!-- 段落 -->
                    <template v-else-if="element.type === 'paragraph'">
                      <div
                        :style="{ textAlign: element.props.align, fontSize: element.props.fontSize + 'px', color: element.props.color, lineHeight: element.props.lineHeight }">
                        {{ element.props.text }}
                      </div>
                    </template>

                    <!-- 单图（等比例，不裁切） -->
                    <template v-else-if="element.type === 'image'">
                      <div :style="{ borderRadius: (element.props.radius || 0) + 'px', overflow: 'hidden' }">
                        <img :src="element.props.url" :style="imgStyle(element.props)" />
                      </div>
                    </template>

                    <!-- 轮播 -->
                    <template v-else-if="element.type === 'carousel'">
                      <div
                        :style="{ height: (element.props.height || 420) + 'px', background: '#f6f7f9', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px', overflow: 'hidden' }">
                        <span v-if="!element.props.images.length">[轮播预览]</span>
                        <img v-else :src="element.props.images[0]" style="max-width:100%; max-height:100%;" />
                      </div>
                    </template>

                    <!-- 按钮（透明容器，宽 100%） -->
                    <template v-else-if="element.type === 'button'">
                      <button :style="{
                        width: '100%', height: '44px', border: 'none',
                        borderRadius: (element.props.shape === 'pill' ? '999px' : (element.props.shape === 'round' ? '12px' : '4px')),
                        background: element.props.bgColor || 'linear-gradient(135deg,#1ec97d,#09ab4f)',
                        color: element.props.textColor || '#fff', fontSize: '16px',
                        boxShadow: '0 12px 24px rgba(30,201,125,.18)'
                      }">{{ element.props.text }}</button>
                    </template>

                    <!-- 视频（横版，默认 contain 完整显示） -->
                    <template v-else-if="element.type === 'video'">
                      <div :style="videoBoxStyle(element.props)">
                        <video :src="element.props.url" :poster="element.props.cover"
                          :autoplay="!!element.props.autoplay" :controls="element.props.controls !== false"
                          :style="{ width: '100%', height: '100%', objectFit: (element.props.fit || 'contain') }" />
                      </div>
                    </template>

                    <!-- 单商品（卡片，按钮固定底部） -->
                    <template v-else-if="element.type === 'product_single'">
                      <div class="card-single">
                        <img :src="element.props.product?.image" class="card-thumb" />
                        <div class="card-info">
                          <div class="card-title">{{ element.props.product?.title || '示例商品' }}</div>
                          <div class="card-row">
                            <div class="card-price">¥{{ element.props.product?.price ?? '0.00' }}</div>
                            <button class="card-buy">购买</button>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- 商品宫格（2/3 列，按钮固定底部） -->
                    <template v-else-if="element.type === 'product_grid'">
                      <div :class="['grid-wrap', 'cols-' + (element.props.columns || 2)]">
                        <div class="g-card" v-for="(p, i) in element.props.products" :key="i">
                          <div class="g-img" :style="gridImgStyle(element.props)">
                            <img :src="p.image" />
                          </div>
                          <div class="g-body">
                            <div class="g-title text-2line">{{ p.title || '商品' }}</div>
                            <div class="g-row">
                              <div class="g-price" :class="{ 'small': (element.props.columns || 2) === 3 }">¥{{ p.price ??
                                '0.00' }}</div>
                              <button class="g-buy" :class="{ 'small': (element.props.columns || 2) === 3 }">购买</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- 未知 -->
                    <template v-else>
                      <div style="color:#999">[未知组件 {{ element.type }}]</div>
                    </template>
                  </div>
                </div>
              </template>
            </draggable>
            <div v-if="blocks.length === 0" class="empty-tip">点击左侧组件添加到页面</div>
          </div>
        </div>
      </div>

      <!-- 属性面板 -->
      <div class="panel inspector" v-if="current">
        <div class="panel-title">属性：{{ typeName(current.type) }}</div>

        <div class="form-scroll">
          <el-form label-width="100px" class="form">
            <el-form-item label="组件ID"><el-input v-model="current.id" disabled /></el-form-item>
            <el-divider>内容</el-divider>

            <!-- H1 -->
            <template v-if="current.type === 'h1'">
              <el-form-item label="文本"><el-input v-model="current.props.text" /></el-form-item>
              <el-form-item label="大小"><el-input-number v-model="current.props.fontSize" :min="12"
                  :max="64" /></el-form-item>
              <el-form-item label="颜色"><el-color-picker v-model="current.props.color" /></el-form-item>
              <el-form-item label="对齐">
                <el-select v-model="current.props.align" style="width:160px">
                  <el-option label="左" value="left" /><el-option label="中" value="center" /><el-option label="右"
                    value="right" />
                </el-select>
              </el-form-item>
            </template>

            <!-- 段落 -->
            <template v-else-if="current.type === 'paragraph'">
              <el-form-item label="内容"><el-input v-model="current.props.text" type="textarea"
                  :rows="4" /></el-form-item>
              <el-form-item label="大小"><el-input-number v-model="current.props.fontSize" :min="12"
                  :max="32" /></el-form-item>
              <el-form-item label="行高"><el-input-number v-model="current.props.lineHeight" :min="1.2" :max="2.4"
                  :step="0.1" /></el-form-item>
              <el-form-item label="颜色"><el-color-picker v-model="current.props.color" /></el-form-item>
              <el-form-item label="对齐">
                <el-select v-model="current.props.align" style="width:160px">
                  <el-option label="左" value="left" /><el-option label="中" value="center" /><el-option label="右"
                    value="right" />
                </el-select>
              </el-form-item>
            </template>

            <!-- 单图 -->
            <template v-else-if="current.type === 'image'">
              <el-form-item label="图片">
                <el-upload :show-file-list="false" :before-upload="beforeFile"
                  :http-request="(opt) => uploadAsset(opt, 'image')">
                  <el-button>上传图片</el-button>
                </el-upload>
                <div v-if="current.props.url" class="thumb"><img :src="current.props.url" /></div>
              </el-form-item>
              <el-form-item label="圆角"><el-input-number v-model="current.props.radius" :min="0"
                  :max="48" /></el-form-item>
              <el-form-item label="高度"><el-input-number v-model="current.props.height" :min="100" :max="1200" />
                px</el-form-item>
              <el-form-item label="缩放">
                <el-select v-model="current.props.mode" style="width:160px">
                  <el-option label="等比例（widthFix）" value="widthFix" />
                  <el-option label="裁切铺满（cover）" value="cover" />
                </el-select>
              </el-form-item>
            </template>

            <!-- 轮播 -->
            <template v-else-if="current.type === 'carousel'">
              <el-form-item label="轮播图">
                <div class="imgs">
                  <div class="img" v-for="(it, i) in current.props.images" :key="i">
                    <img :src="it" />
                    <el-button text type="danger" @click="current.props.images.splice(i, 1)">移除</el-button>
                  </div>
                  <el-upload :show-file-list="false" :before-upload="beforeFile"
                    :http-request="(opt) => uploadCarousel(opt)">
                    <el-button>添加图片</el-button>
                  </el-upload>
                </div>
              </el-form-item>
              <el-form-item label="高度"><el-input-number v-model="current.props.height" :min="120" :max="1200" />
                px</el-form-item>
              <el-form-item label="自动播"><el-switch v-model="current.props.autoplay" /></el-form-item>
              <el-form-item label="间隔"><el-input-number v-model="current.props.interval" :min="1000" :max="10000"
                  :step="500" /> ms</el-form-item>
            </template>

            <!-- 按钮 -->
            <template v-else-if="current.type === 'button'">
              <el-form-item label="文案"><el-input v-model="current.props.text" /></el-form-item>
              <el-form-item label="形状">
                <el-select v-model="current.props.shape" style="width:160px">
                  <el-option label="直角" value="square" /><el-option label="圆角" value="round" /><el-option label="胶囊"
                    value="pill" />
                </el-select>
              </el-form-item>
              <el-form-item label="背景色"><el-color-picker v-model="current.props.bgColor" /></el-form-item>
              <el-form-item label="文字色"><el-color-picker v-model="current.props.textColor" /></el-form-item>
              <el-divider>点击行为</el-divider>
              <el-form-item label="类型">
                <el-select v-model="current.props.linkType" style="width:200px">
                  <el-option label="外链" value="url" /><el-option label="跳转路径" value="navigate" /><el-option label="示例支付"
                    value="pay" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="current.props.linkType !== 'pay'" label="值">
                <el-input v-model="current.props.linkValue" placeholder="https:// 或 /pages/xxx/xxx" />
              </el-form-item>
            </template>

            <!-- 视频 -->
            <template v-else-if="current.type === 'video'">
              <el-form-item label="封面">
                <el-upload :show-file-list="false" :before-upload="beforeFile"
                  :http-request="(opt) => uploadVideoCover(opt)">
                  <el-button>上传封面</el-button>
                </el-upload>
                <div v-if="current.props.cover" class="thumb"><img :src="current.props.cover" /></div>
              </el-form-item>
              <el-form-item label="视频">
                <el-upload :show-file-list="false" :before-upload="(f) => beforeFile(f, { maxSize: 30 })"
                  :http-request="(opt) => uploadAsset(opt, 'video')">
                  <el-button>上传视频</el-button>
                </el-upload>
                <div v-if="current.props.url" class="file-url">{{ current.props.url }}</div>
              </el-form-item>
              <el-form-item label="自动播"><el-switch v-model="current.props.autoplay" /></el-form-item>
              <el-form-item label="控件"><el-switch v-model="current.props.controls" /></el-form-item>
              <el-form-item label="适应">
                <el-select v-model="current.props.fit" style="width:160px">
                  <el-option label="完整显示（contain）" value="contain" />
                  <el-option label="裁切铺满（cover）" value="cover" />
                </el-select>
              </el-form-item>
              <el-form-item label="高度"><el-input-number v-model="current.props.height" :min="120" :max="1200" />
                px</el-form-item>
            </template>

            <!-- 单商品 -->
            <template v-else-if="current.type === 'product_single'">
              <el-form-item label="样式">
                <el-select v-model="current.props.style" style="width:200px"><el-option label="卡片"
                    value="card" /><el-option label="简洁" value="simple" /></el-select>
              </el-form-item>
              <el-form-item label="商品">
                <div class="row-gap">
                  <el-input v-model="current.props.productId" placeholder="商品ID" style="width:160px" />
                  <el-button @click="openGoodsPicker('single')">选择商品</el-button>
                </div>
              </el-form-item>
              <el-form-item label="标题"><el-input v-model="current.props.product.title"
                  placeholder="可覆盖" /></el-form-item>
              <el-form-item label="图片">
                <el-upload :show-file-list="false" :before-upload="beforeFile"
                  :http-request="(opt) => uploadProductImage(opt)">
                  <el-button>上传图片</el-button>
                </el-upload>
                <div v-if="current.props.product.image" class="thumb"><img :src="current.props.product.image" /></div>
              </el-form-item>
              <el-form-item label="价格"><el-input-number v-model="current.props.product.price" :min="0" /></el-form-item>
            </template>

            <!-- 商品宫格 -->
            <template v-else-if="current.type === 'product_grid'">
              <el-form-item label="风格">
                <el-select v-model="current.props.style" style="width:200px"><el-option label="宫格A"
                    value="grid-A" /><el-option label="宫格B" value="grid-B" /></el-select>
              </el-form-item>
              <el-form-item label="列数"><el-input-number v-model="current.props.columns" :min="2"
                  :max="3" /></el-form-item>
              <el-form-item label="显示销量"><el-switch v-model="current.props.showSales" /></el-form-item>

              <el-divider>商品集合</el-divider>
              <div class="row-gap"><el-button @click="openGoodsPicker('multi')">批量选择商品</el-button></div>

              <div v-for="(p, i) in current.props.products" :key="i" class="prod-line">
                <el-input v-model="p.productId" placeholder="商品ID" style="width:140px" class="mr8" />
                <el-input v-model="p.title" placeholder="标题" style="width:180px" class="mr8" />
                <el-input v-model="p.image" placeholder="图片URL" style="width:220px" class="mr8" />
                <el-input-number v-model="p.price" :min="0" />
                <el-button text type="danger" @click="current.props.products.splice(i, 1)">移除</el-button>
              </div>
            </template>

            <el-divider>样式 (支持四边)</el-divider>
            <div class="quad">
              <div class="quad-title">margin</div>
              <el-input v-model="current.style.marginTop" placeholder="top 如 12px" />
              <el-input v-model="current.style.marginRight" placeholder="right" />
              <el-input v-model="current.style.marginBottom" placeholder="bottom" />
              <el-input v-model="current.style.marginLeft" placeholder="left" />
            </div>
            <div class="quad">
              <div class="quad-title">padding</div>
              <el-input v-model="current.style.paddingTop" placeholder="top 如 12px" />
              <el-input v-model="current.style.paddingRight" placeholder="right" />
              <el-input v-model="current.style.paddingBottom" placeholder="bottom" />
              <el-input v-model="current.style.paddingLeft" placeholder="left" />
            </div>
            <el-form-item label="背景"><el-color-picker v-model="current.style.backgroundColor" /></el-form-item>
            <el-form-item label="圆角"><el-input v-model="current.style.borderRadius" placeholder="12px" /></el-form-item>
            <el-form-item label="阴影"><el-input v-model="current.style.boxShadow"
                placeholder="0 4px 12px rgba(0,0,0,.08)" /></el-form-item>
          </el-form>
        </div>
      </div>
    </div>

    <!-- 我的页面 -->
    <el-dialog v-model="pageDialog" title="我的页面" width="760px">
      <el-table :data="myPages" height="380">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="名称" />
        <el-table-column prop="route_key" label="路由" width="120" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.is_active ? 'success' : (row.status === 2 ? 'warning' : 'info')">
              {{ row.is_active ? '生效' : (row.status === 2 ? '已发布' : '草稿') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="更新时间" width="170" />
        <el-table-column label="操作" width="240">
          <template #default="{ row }">
            <el-button size="small" @click="openPage(row)">打开</el-button>
            <el-button size="small" type="primary" @click="quickPublish(row)">发布</el-button>
            <el-popconfirm title="确定删除该页面？" @confirm="removeById(row)">
              <template #reference><el-button size="small" type="danger">删除</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
      <template #footer><el-button @click="pageDialog = false">关闭</el-button></template>
    </el-dialog>

    <!-- 模板库 -->
    <el-dialog v-model="tplDialog" title="模板库" width="820px">
      <div class="tpl-grid">
        <div class="tpl-item" v-for="tpl in templates" :key="tpl.id">
          <img v-if="tpl.thumb" :src="tpl.thumb" />
          <div class="tpl-meta">
            <div class="tpl-name">{{ tpl.name }}</div>
            <div class="tpl-desc">{{ tpl.description || '—' }}</div>
            <el-button size="small" @click="applyTpl(tpl)" :disabled="!miniId">应用</el-button>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="saveAsTemplate">当前页面另存为模板</el-button>
        <el-button @click="tplDialog = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 选择商品 -->
    <el-dialog v-model="goodsDialog.visible" title="选择商品" width="880px">
      <div class="goods-toolbar">
        <el-input v-model="goodsQuery.q" placeholder="搜名称/编码" style="width:260px" @keyup.enter="loadGoods(1)" />
        <el-button @click="loadGoods(1)">搜索</el-button>
      </div>
      <el-table :data="goodsList" v-loading="goodsLoading" height="420" @row-click="handleGoodsRowClick"
        :row-class-name="row => (goodsDialog.mode === 'single' ? 'row-clickable' : '')">
        <el-table-column v-if="goodsDialog.mode === 'multi'" type="selection" width="48" />
        <el-table-column label="图片" width="90">
          <template #default="{ row }"><img :src="row.image"
              style="width:64px;height:64px;object-fit:cover;border-radius:8px" /></template>
        </el-table-column>
        <el-table-column prop="name" label="名称" />
        <el-table-column prop="price" label="价格" width="120">
          <template #default="{ row }"><span style="color:#f40;font-weight:700">¥{{ row.price ?? '0.00'
              }}</span></template>
        </el-table-column>
        <el-table-column prop="sales" label="销量" width="100" />
      </el-table>
      <div style="display:flex;justify-content:flex-end;margin-top:10px">
        <el-pagination layout="prev, pager, next, jumper" :page-size="goodsQuery.page_size"
          :current-page="goodsQuery.page" :total="goodsTotal" @current-change="loadGoods" />
      </div>
      <template #footer>
        <el-button @click="goodsDialog.visible = false">取消</el-button>
        <el-button type="primary" v-if="goodsDialog.mode === 'multi'" @click="confirmGoodsMulti">添加</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import draggable from 'vuedraggable'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Edit, Document, Picture, VideoCamera, ShoppingCart, Grid as GridIcon, MagicStick, Tickets } from '@element-plus/icons-vue'
import { getUploadSignature, uploadToOSS, beforeUpload } from '@/utils/uploadUtils'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id
const agentId = userInfo.agent_id || null

const minis = ref([])
const miniId = ref(null)

const routeKey = ref('home')
const pageName = ref('未命名页面')
const pageId = ref(null)
const blocks = ref([])
const activeId = ref(null)

const saving = ref(false)
const publishing = ref(false)
const pageDialog = ref(false)
const myPages = ref([])
const tplDialog = ref(false)
const templates = ref([])

const current = computed(() => {
  const arr = Array.isArray(blocks.value) ? blocks.value : []
  return arr.find(b => b.id === activeId.value) || null
})


// 授权状态
const diyAuthorized = ref(true)
const authChecked = ref(false)

// 解析 application_market
const parseApplicationMarket = (marketStr) => {
  try { return JSON.parse(marketStr || '{}') } catch { return {} }
}

// 查询授权：调用 shop_api.php?action=query
async function checkDiyAuth() {
  try {
    const account = (userInfo && userInfo.account) || ''
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/shop_api.php`, {
      action: 'query',
      account
    })
    const market = parseApplicationMarket(data?.data?.application_market)
    const v = market?.qyxcx_diy
    // 允许 true / 1 / '1' / 'true' 视为已授权
    const ok = v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true'
    diyAuthorized.value = !!ok
  } catch (e) {
    // 接口失败按未授权处理
    diyAuthorized.value = false
  } finally {
    authChecked.value = true
  }
}


/** 组件清单（调整 image/video 的默认值更贴近前端） */
const catalog = [
  { type: 'h1', name: 'H1 标题', icon: Edit, defaults: () => ({ text: '欢迎来到我的小商店', color: '#111', fontSize: 36, align: 'center' }) },
  { type: 'paragraph', name: '段落', icon: Document, defaults: () => ({ text: '这是一段介绍文案', color: '#666', fontSize: 14, lineHeight: 1.6, align: 'left' }) },
  { type: 'image', name: '单图', icon: Picture, defaults: () => ({ url: '', radius: 16, height: 320, mode: 'widthFix' }) },
  { type: 'carousel', name: '轮播图', icon: Tickets, defaults: () => ({ images: [], height: 420, autoplay: true, interval: 3000 }) },
  { type: 'button', name: '按钮', icon: MagicStick, defaults: () => ({ text: '立即购买', shape: 'round', bgColor: '#09AB4F', textColor: '#fff', linkType: 'url', linkValue: '' }) },
  { type: 'video', name: '视频', icon: VideoCamera, defaults: () => ({ url: '', cover: '', autoplay: false, controls: true, height: 236, fit: 'contain' }) },
  { type: 'product_single', name: '单商品', icon: ShoppingCart, defaults: () => ({ style: 'card', productId: '', product: { title: '', image: '', price: null } }) },
  { type: 'product_grid', name: '商品宫格', icon: GridIcon, defaults: () => ({ style: 'grid-A', columns: 2, showSales: true, products: [] }) }
]

const uuid = () => 'b_' + Math.random().toString(36).slice(2, 10)
function addBlock(c) {
  blocks.value.push({
    id: uuid(), type: c.type, props: c.defaults(),
    style: {
      marginTop: '12px', marginRight: '12px', marginBottom: '12px', marginLeft: '12px',
      paddingTop: '12px', paddingRight: '12px', paddingBottom: '12px', paddingLeft: '12px',
      backgroundColor: '#fff', borderRadius: '12px', boxShadow: ''
    }
  })
}
function typeName(t) { const it = catalog.find(x => x.type === t); return it ? it.name : t; }
function copyBlock(i) { const raw = JSON.parse(JSON.stringify(blocks.value[i])); raw.id = uuid(); blocks.value.splice(i + 1, 0, raw); activeId.value = raw.id }
function removeBlock(i) { blocks.value.splice(i, 1); if (!blocks.value.length) activeId.value = null }
function moveUp(i) { if (i > 0) { const x = blocks.value.splice(i, 1)[0]; blocks.value.splice(i - 1, 0, x) } }
function moveDown(i) { if (i < blocks.value.length - 1) { const x = blocks.value.splice(i, 1)[0]; blocks.value.splice(i + 1, 0, x) } }

/** ===== 预览渲染工具（与小程序端一致） ===== */
const isCardType = (t) => t === 'h1' || t === 'product_single' || t === 'product_grid' || t === 'paragraph'

function parseMarginAll(v) {
  if (!v && v !== 0) return { top: '', right: '', bottom: '', left: '' }
  const arr = String(v).trim().split(/\s+/)
  const a = arr[0], b = arr[1], c = arr[2], d = arr[3]
  if (arr.length === 1) return { top: a, right: a, bottom: a, left: a }
  if (arr.length === 2) return { top: a, right: b, bottom: a, left: b }
  if (arr.length === 3) return { top: a, right: b, bottom: c, left: b }
  return { top: a, right: b, bottom: c, left: d }
}

/** 画布容器样式：卡片 vs 非卡片 */
function blockStyle(el) {
  const s = el?.style || {}
  const t = el?.type
  const card = isCardType(t)
  const st = {}

  // 统一左右留白由 safe-area 控制，这里固定 0
  st.marginLeft = '0px'
  st.marginRight = '0px'

  // 解析 margin 简写，仅取上下
  if (s.margin) {
    const m = parseMarginAll(s.margin)
    st.marginTop = m.top || s.marginTop || '12px'
    st.marginBottom = m.bottom || s.marginBottom || '12px'
  } else {
    st.marginTop = s.marginTop || '12px'
    st.marginBottom = s.marginBottom || '12px'
  }

  if (card) {
    st.backgroundColor = s.backgroundColor || '#fff'
    st.borderRadius = s.borderRadius || (t === 'h1' ? '16px' : '12px')
    st.boxShadow = s.boxShadow || ''

    if (s.padding) st.padding = s.padding
    st.paddingTop = s.paddingTop ?? s.pt ?? (t === 'h1' ? '24px' : '12px')
    st.paddingRight = s.paddingRight ?? s.pr ?? (t === 'h1' ? '20px' : '12px')
    st.paddingBottom = s.paddingBottom ?? s.pb ?? (t === 'h1' ? '24px' : '12px')
    st.paddingLeft = s.paddingLeft ?? s.pl ?? (t === 'h1' ? '20px' : '12px')
  } else {
    st.backgroundColor = 'transparent'
    st.borderRadius = s.borderRadius || '0'
    st.boxShadow = ''
    st.padding = 0
  }
  return st
}

/** 单图：等比例 widthFix，不裁切；cover 时按给定高度铺满 */
function imgStyle(p = {}) {
  const mode = (p.mode || 'widthFix').toLowerCase()
  if (mode === 'widthfix') return { width: '100%', display: 'block' }
  const h = p.height ? (p.height + 'px') : '236px'
  return { width: '100%', height: h, display: 'block', objectFit: 'cover' }
}

/** 视频容器：默认 16:9 高度，圆角、溢出隐藏 */
function videoBoxStyle(p = {}) {
  const h = p.height ? (p.height + 'px') : '236px'
  return { background: '#000', borderRadius: '16px', overflow: 'hidden', height: h }
}

/** 宫格图片高度：2 列 160px，3 列 120px */
function gridImgStyle(props = {}) {
  const cols = Math.max(2, Math.min(3, Number(props.columns || 2)))
  const h = cols === 3 ? '120px' : '160px'
  return { height: h }
}

/** ===== 上传相关 ===== */
async function beforeFile(file, opt = { maxSize: 2 }) { return await beforeUpload(file, opt) }
async function uploadAsset(options, type = 'image') {
  const sig = await getUploadSignature(type); const ret = await uploadToOSS(options.file, sig)
  if (current.value) {
    if (current.value.type === 'image') current.value.props.url = ret.url
    else if (current.value.type === 'video') current.value.props.url = ret.url
    else if (current.value.type === 'product_single') current.value.props.product.image = ret.url
  }
  options.onSuccess({ url: ret.url })
}
async function uploadCarousel(options) { const sig = await getUploadSignature('image'); const ret = await uploadToOSS(options.file, sig); current.value.props.images.push(ret.url); options.onSuccess({ url: ret.url }) }
async function uploadVideoCover(options) { const sig = await getUploadSignature('image'); const ret = await uploadToOSS(options.file, sig); current.value.props.cover = ret.url; options.onSuccess({ url: ret.url }) }
async function uploadProductImage(options) { const sig = await getUploadSignature('image'); const ret = await uploadToOSS(options.file, sig); current.value.props.product.image = ret.url; options.onSuccess({ url: ret.url }) }

function miniLabel(m) { return `${m.platform_name || '小红书'}｜${m.app_id}｜mini_id=${m.mini_id}` }

async function loadMinis() {
  try {
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/mini_diy.php`, {
      params: { action: 'list_minis', store_id: storeId, agent_id: agentId }
    })
    if (data.success) {
      minis.value = data.data.list || []
      if (minis.value.length === 1) { miniId.value = minis.value[0].mini_id; await loadPages() }
    } else {
      ElMessage.error(data.message || '加载小程序列表失败')
    }
  } catch { ElMessage.error('请求失败：小程序列表') }
}
function onMiniChange() { createNew(); loadPages() }

/** 保存/发布 */
async function saveDraft() {
  if (!storeId) return ElMessage.error('缺少 store_id')
  if (!miniId.value) return ElMessage.error('请选择一个小程序')
  const payload = {
    id: pageId.value || undefined,
    store_id: storeId,
    agent_id: agentId || undefined,
    mini_id: miniId.value,
    name: pageName.value || '未命名页面',
    route_key: routeKey.value || 'home',
    cover: null,
    description: null,
    content: blocks.value
  }
  try {
    saving.value = true
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/mini_diy.php`, { action: 'save_page', ...payload })
    if (data.success) { pageId.value = data.data.id; ElMessage.success('保存成功') } else ElMessage.error(data.message || '保存失败')
  } finally { saving.value = false }
}
async function publish() {
  if (!pageId.value) { await saveDraft(); if (!pageId.value) return }
  try {
    publishing.value = true
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/mini_diy.php`, { action: 'publish_page', page_id: pageId.value, store_id: storeId, agent_id: agentId, mini_id: miniId.value })
    if (data.success) { ElMessage.success('发布成功') } else ElMessage.error(data.message || '发布失败')
  } finally { publishing.value = false }
}

function toBlocks(val) {
  try {
    const parsed = typeof val === 'string' ? JSON.parse(val) : val
    return Array.isArray(parsed) ? parsed : []
  } catch { return [] }
}

async function loadPages() {
  if (!miniId.value) return
  pageDialog.value = true
  const { data } = await axios.get(`${config.BASE_URL}/shop/api/mini_diy.php`, { params: { action: 'list_pages', store_id: storeId, agent_id: agentId, mini_id: miniId.value, route_key: routeKey.value } })
  myPages.value = data.success ? (data.data.list || []) : []
}
function openPage(row) {
  pageId.value = row.id; pageName.value = row.name; routeKey.value = row.route_key
  blocks.value = toBlocks(row.content)
  activeId.value = blocks.value[0]?.id || null
  pageDialog.value = false
}
async function quickPublish(row) {
  await axios.post(`${config.BASE_URL}/shop/api/mini_diy.php`, { action: 'publish_page', page_id: row.id, store_id: storeId, agent_id: agentId, mini_id: miniId.value })
  ElMessage.success('已发布'); loadPages()
}
async function removeById(row) {
  await axios.delete(`${config.BASE_URL}/shop/api/mini_diy.php`, { data: { action: 'delete_page', page_id: row.id, store_id: storeId, agent_id: agentId, mini_id: miniId.value } })
  ElMessage.success('已删除'); loadPages()
}
function createNew() { pageId.value = null; pageName.value = '未命名页面'; blocks.value = []; activeId.value = null }

/** 模板库 */
async function openTemplateLib() {
  tplDialog.value = true
  const { data } = await axios.get(`${config.BASE_URL}/shop/api/mini_diy.php`, { params: { action: 'list_templates', store_id: storeId } })
  templates.value = data.success ? (data.data.list || []) : []
}
async function applyTpl(tpl) {
  if (!miniId.value) return ElMessage.warning('请先选择要应用的目标小程序')
  const name = await ElMessageBox.prompt('输入新页面名称', '应用模板', { inputValue: tpl.name, confirmButtonText: '应用', cancelButtonText: '取消' }).then(r => r.value).catch(() => null)
  if (!name) return;
  const { data } = await axios.post(`${config.BASE_URL}/shop/api/mini_diy.php`, { action: 'apply_template', template_id: tpl.id, store_id: storeId, agent_id: agentId, mini_id: miniId.value, name, route_key: routeKey.value })
  if (data.success) {
    ElMessage.success('模板已应用')
    const res = await axios.get(`${config.BASE_URL}/shop/api/mini_diy.php`, { params: { action: 'get_page', page_id: data.data.page_id } })
    if (res.data.success) {
      const row = res.data.data
      pageId.value = row.id; pageName.value = name
      blocks.value = toBlocks(row.content)
      activeId.value = blocks.value[0]?.id || null
    }
  } else ElMessage.error(data.message || '应用失败')
}
async function saveAsTemplate() {
  if (!blocks.value.length) return ElMessage.warning('当前页面为空')
  const name = await ElMessageBox.prompt('输入模板名称', '另存为模板', { inputValue: pageName.value, confirmButtonText: '保存', cancelButtonText: '取消' }).then(r => r.value).catch(() => null)
  if (!name) return;
  const { data } = await axios.post(`${config.BASE_URL}/shop/api/mini_diy.php`, { action: 'save_template', store_id: storeId, name, is_public: 0, content: blocks.value })
  if (data.success) ElMessage.success('模板已保存'); else ElMessage.error(data.message || '保存失败')
}

/** 商品选择 */
const goodsDialog = ref({ visible: false, mode: 'single' })
const goodsList = ref([])
const goodsLoading = ref(false)
const goodsTotal = ref(0)
const goodsQuery = ref({ q: '', page: 1, page_size: 10 })

function openGoodsPicker(mode = 'single') {
  if (!miniId.value) return ElMessage.warning('请先选择小程序')
  goodsDialog.value = { visible: true, mode }
  loadGoods(1)
}
async function loadGoods(p) {
  goodsLoading.value = true
  if (typeof p === 'number') goodsQuery.value.page = p
  try {
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/mini_diy.php`, {
      params: { action: 'list_goods', store_id: storeId, mini_id: miniId.value, q: goodsQuery.value.q, page: goodsQuery.value.page, page_size: goodsQuery.value.page_size }
    })
    if (data.success) {
      goodsList.value = data.data.list || []
      goodsTotal.value = data.data.total || 0
    } else {
      goodsList.value = []; goodsTotal.value = 0
    }
  } finally { goodsLoading.value = false }
}
function handleGoodsRowClick(row) {
  if (goodsDialog.value.mode !== 'single') return
  if (!current.value) return
  if (current.value.type === 'product_single') {
    current.value.props.productId = row.id
    current.value.props.product = { title: row.name, image: row.image, price: row.price }
  }
  goodsDialog.value.visible = false
}
function confirmGoodsMulti() {
  if (!current.value || current.value.type !== 'product_grid') return
  const exist = new Set(current.value.props.products.map(p => String(p.productId || '')))
  goodsList.value.forEach(row => {
    const idStr = String(row.id)
    if (exist.has(idStr)) return
    current.value.props.products.push({ productId: row.id, title: row.name, image: row.image, price: row.price })
    exist.add(idStr)
  })
  goodsDialog.value.visible = false
}

onMounted(() => {
  checkDiyAuth()
  loadMinis()
})
</script>

<style scoped>
/* 布局与质感 */
.diy-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #fff;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 2;
}

/* 三栏布局 */
.workspace {
  display: grid;
  grid-template-columns: 280px 1fr 380px;
  gap: 12px;
  padding: 12px;
  height: calc(100vh - 64px);
  box-sizing: border-box;
}

.panel {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .03);
}

.panel .panel-title {
  padding: 12px;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}

/* 组件库（可滚动） */
.palette {
  width: 260px;
}

.palette-list {
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  overflow: auto;
  max-height: calc(100vh - 160px);
}

.palette-item {
  border: 1px dashed #ddd;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.palette-item:hover {
  background: #f7f9fb;
}

.palette-item .icon {
  width: 36px;
  height: 36px;
  background: #f2f3f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

/* 画布 */
.canvas {
  display: flex;
  justify-content: center;
}

.phone {
  width: 420px;
  background: #f0f2f5;
  border: 12px solid #333;
  border-radius: 24px;
  position: relative;
}

.safe-area {
  height: 100%;
  overflow: auto;
  padding: 12px;
}

.block {
  position: relative;
  margin: 8px 0;
}

.block .toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 8px;
  border-bottom: 1px dashed #eee;
  background: #fff;
  border-radius: 12px 12px 0 0;
}

.block .toolbar .drag-handle {
  cursor: move;
  color: #999;
  margin-right: auto;
}

/* 真正显示样式的容器（样式通过 :style 注入） */
.block-body {
  border-radius: 0 0 12px 12px;
}

/* 高亮 */
.block.active .block-body {
  outline: 2px solid #409eff;
  outline-offset: 0;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

/* 属性面板可滚动 */
.inspector {
  display: flex;
  flex-direction: column;
}

.form-scroll {
  overflow: auto;
  height: 100%;
}

.form {
  padding: 12px;
}

.row-gap {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.thumb img {
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 8px;
  margin-top: 8px;
  border: 1px solid #eee;
}

.imgs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.imgs .img {
  width: 120px;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}

.imgs .img img {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

/* 四边输入块 */
.quad {
  display: grid;
  grid-template-columns: 80px 1fr 1fr 1fr 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.quad-title {
  color: #666;
  text-align: right;
  padding-right: 8px;
}

/* 模板库 */
.tpl-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.tpl-item {
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tpl-item img {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.tpl-meta {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tpl-name {
  font-weight: 700;
}

.tpl-desc {
  color: #666;
  font-size: 13px;
  min-height: 32px;
}

/* 商品弹窗 */
.goods-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.row-clickable {
  cursor: pointer;
}

/* 小工具 */
.mr8 {
  margin-right: 8px;
}

/* ===== 与小程序端一致的卡片/宫格样式 ===== */
.card-single {
  display: flex;
  gap: 12px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 16px 32px rgba(0, 0, 0, .06);
}

.card-thumb {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  background: #f6f7f9;
}

.card-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 120px;
}

.card-title {
  font-weight: 700;
  color: #222;
  font-size: 16px;
  line-height: 1.4;
}

.card-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.card-price {
  color: #e02e24;
  font-weight: 800;
  font-size: 18px;
}

.card-buy {
  height: 36px;
  padding: 0 16px;
  background: linear-gradient(135deg, #1ec97d, #09ab4f);
  color: #fff;
  border: none;
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(9, 171, 79, .24);
}

/* 宫格（2/3 列） */
.grid-wrap {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px;
}

.grid-wrap .g-card {
  width: calc(100% / 2);
  padding: 0 10px;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.grid-wrap.cols-3 .g-card {
  width: calc(100% / 3);
}

.g-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  overflow: hidden;
}

.g-img {
  width: 100%;
  background: #f6f7f9;
}

.g-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.g-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  min-height: 130px;
}

.grid-wrap.cols-3 .g-body {
  min-height: 118px;
}

.g-title {
  color: #222;
  font-size: 14px;
}

.g-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.g-price {
  color: #e02e24;
  font-weight: 800;
  font-size: 16px;
}

.g-price.small {
  font-size: 14px;
}

.g-buy {
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, #1ec97d, #09ab4f);
}

.g-buy.small {
  height: 26px;
  padding: 0 10px;
  font-size: 12px;
}

/* 文本工具 */
.text-2line {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.unauth-mask {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .45);
}

.unauth-dialog {
  background: #fff;
  border-radius: 16px;
  padding: 24px 28px;
  width: 88%;
  max-width: 520px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, .18);
}

.unauth-title {
  font-size: 18px;
  font-weight: 800;
  color: #e02e24;
  margin-bottom: 8px;
}

.unauth-desc {
  font-size: 14px;
  color: #555;
}
</style>